import React, { useState, useEffect } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Input from "@amzn/meridian/input";
import Select from "@amzn/meridian/select";
import SelectOption from "@amzn/meridian/select/select-option";
import { Button, Divider, Icon, Sheet } from "@amzn/meridian";
import closeTokens from "@amzn/meridian-tokens/base/icon/close-small";
import { SdsAttribute } from "src/types/classificationTypes";
import PillButton from "../../UI/PillButton";

const PHYSICAL_STATE_OPTIONS = ["Liquid", "Solid", "Gas", "Aerosol"];
const REACTIVITY_OPTIONS = ["Stable", "Unstable"];

export const FIXED_SDS_ATTRIBUTES: { label: string }[] = [
  { label: "Physical State" },
  { label: "pH (Numeric)" },
  { label: "Reactivity" },
  { label: "Flash Point (C)" },
  { label: "UN ID" },
  { label: "CAS" },
  { label: "Ingredients" },
];

type AttributeInputType = "physical_state" | "reactivity" | "ph_numeric" | "text";

const ATTRIBUTE_INPUT_TYPES: Record<string, AttributeInputType> = {
  "Physical State": "physical_state",
  Reactivity: "reactivity",
  "pH (Numeric)": "ph_numeric",
};

const getInputType = (attributeName: string): AttributeInputType =>
  ATTRIBUTE_INPUT_TYPES[attributeName] ?? "text";

interface EditAttributesSheetProps {
  open: boolean;
  sdsAttributes?: SdsAttribute[];
  savedOverrides: Record<string, string>;
  onSave: (newOverrides: Record<string, string>) => void;
  onCancel: () => void;
}

const EditAttributesSheet: React.FC<EditAttributesSheetProps> = ({
  open,
  sdsAttributes,
  savedOverrides,
  onSave,
  onCancel,
}) => {
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [phError, setPhError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const webResearchedAttributes: Record<string, SdsAttribute> = {};
    (sdsAttributes ?? []).forEach((attr) => {
      webResearchedAttributes[attr.name] = attr;
    });
    const initialValues: Record<string, string> = {};
    FIXED_SDS_ATTRIBUTES.forEach(({ label }) => {
      initialValues[label] = savedOverrides[label] ?? webResearchedAttributes[label]?.value ?? "";
    });
    setDraftValues(initialValues);
    setPhError(null);
  }, [open, sdsAttributes, savedOverrides]);

  const handleSave = () => {
    const phVal = draftValues["pH (Numeric)"] ?? "";
    if (phVal.trim() !== "") {
      const num = Number(phVal);
      if (isNaN(num) || num < 0 || num > 14) {
        setPhError("Enter a value between 0 and 14");
        return;
      }
    }

    const originalValues: Record<string, string> = {};
    (sdsAttributes ?? []).forEach((attr) => {
      originalValues[attr.name] = attr.value;
    });

    const newOverrides: Record<string, string> = {};
    FIXED_SDS_ATTRIBUTES.forEach(({ label }) => {
      const draft = draftValues[label] ?? "";
      if (draft.trim() !== "" && draft !== (originalValues[label] ?? "")) {
        newOverrides[label] = draft;
      }
    });

    onSave(newOverrides);
  };

  const renderInput = (label: string) => {
    const inputType = getInputType(label);
    const value = draftValues[label] ?? "";

    if (inputType === "physical_state") {
      return (
        <Select
          value={value}
          onChange={(val: string) => setDraftValues((prev) => ({ ...prev, [label]: val }))}
          width="100%"
        >
          {PHYSICAL_STATE_OPTIONS.map((opt) => (
            <SelectOption key={opt} value={opt} label={opt} />
          ))}
        </Select>
      );
    }

    if (inputType === "reactivity") {
      return (
        <Select
          value={value}
          onChange={(val: string) => setDraftValues((prev) => ({ ...prev, [label]: val }))}
          width="100%"
        >
          {REACTIVITY_OPTIONS.map((opt) => (
            <SelectOption key={opt} value={opt} label={opt} />
          ))}
        </Select>
      );
    }

    if (inputType === "ph_numeric") {
      return (
        <Column spacing="100">
          <Input
            type="text"
            value={value}
            onChange={(val: string) => {
              setDraftValues((prev) => ({ ...prev, [label]: val }));
              setPhError(null);
            }}
          />
          <Text type="b100" color="secondary">
            0 – 14, decimals accepted
          </Text>
          {phError && (
            <Text type="b100" color="error">
              {phError}
            </Text>
          )}
        </Column>
      );
    }

    return (
      <Input
        type="text"
        value={value}
        onChange={(val: string) => setDraftValues((prev) => ({ ...prev, [label]: val }))}
      />
    );
  };

  return (
    <Sheet type="overlay" side="bottom" open={open} onClose={onCancel} spacingInset="500">
      <Column spacing="400">
        <Row alignmentHorizontal="justify" alignmentVertical="center">
          <Text type="h300">Edit Attribute Values</Text>
          <Button type="icon" onClick={onCancel}>
            <Icon tokens={closeTokens} />
          </Button>
        </Row>
        <Text type="b200" color="secondary">
          Update the values below based on your own sources
        </Text>
        <Divider />
        <Column spacing="400">
          {FIXED_SDS_ATTRIBUTES.map(({ label }) => (
            <Column key={label} spacing="200">
              <Text type="b200">{label}</Text>
              {renderInput(label)}
            </Column>
          ))}
        </Column>
        <Row spacing="300" widths={["fill", "fill"]}>
          <PillButton text="CANCEL" color="outline" onClick={onCancel} />
          <PillButton text="SAVE VALUES" color="success" onClick={handleSave} />
        </Row>
      </Column>
    </Sheet>
  );
};

export default EditAttributesSheet;
